import { ModuleSettingAmounts } from "@models";
import { ElementType, Properties } from "@util/types";
import { ProductDefaultDeserializer } from "./products/ProductDeserializer";
import ProductVariant from "./ProductVariant";
import { CategoryGroup } from "./CategoryGroup";
import { PricingPlan } from "./PricingPlan";

export const rootProductIds = [
	"shortTime",
	"lunch",
	"mobility",
	"spend",
	"web",
	"congrats",
	"fitAndRelax",
	"travel",
	"travel.budget",
	"homeAndWork",
	"sachbezug",
] as const;

// Filled in runtime after loading static data from the backend
let products: Product[] = [];
export const Products = {
	getById(productId: RootProductId): Product {
		const product = products.find((p) => p.id === productId);
		if (!product) {
			throw new Error(`Product ${productId} is not initialized`);
		}
		return product;
	},
	setProducts(productsFromBackend: Product[]) {
		products = productsFromBackend;
	},
};

export type RootProductId = ElementType<typeof rootProductIds>;

export const rootProductDescriptions: {
	[k in RootProductId]: { name: string };
} = {
	shortTime: {
		name: "Kurzarbeit",
	},
	congrats: { name: "Congrats" },
	lunch: { name: "Lunch" },
	mobility: { name: "Mobility" },
	spend: { name: "Spend" },
	web: { name: "Web" },
	fitAndRelax: { name: "Fit & Relax" },
	travel: { name: "Expenses" },
	["travel.budget"]: { name: "Travel Budget" },
	homeAndWork: { name: "Home & Work" },
	sachbezug: { name: "Sachbezug" },
};

export function getRootProductDescription(rootId: RootProductId) {
	return rootProductDescriptions[rootId];
}

export default abstract class Product<
	TSettings extends Record<string, unknown> | null = any
> {
	constructor(props: Properties<Product<TSettings>>) {
		this.name = props.name;
		this.id = props.id;
		this.variants = props.variants;
		this.categoryGroups = props.categoryGroups;
		this.pricingPlans = props.pricingPlans;
	}

	readonly name: string;
	readonly id: RootProductId;
	readonly variants: ProductVariant[];
	readonly categoryGroups: CategoryGroup[];
	readonly pricingPlans: PricingPlan[];

	variantOfId(variantId: string): ProductVariant {
		const variant = this.variants.find((v) => v.id === variantId);
		if (!variant)
			throw new Error(`variant ${variantId} not found on product ${this.id}`);
		return variant;
	}

	getDefaultAmounts(variantId = "default"): ModuleSettingAmounts[] {
		return this.variantOfId(variantId).budgetCategories.map((c) => ({
			category: c.id,
			maxAmountDay: null,
			maxAmountMonth: c.maxAmountMonth ?? 20000,
		}));
	}

	getDefaultCategories(
		variantId = "default",
		enabledCategories?: string[]
	): string[] {
		const variant = this.variantOfId(variantId);

		if (this.id === "spend" || this.id === "congrats") {
			// Spend & congrats must only have one selected category.
			const defaultCategory = variant.budgetCategories[0].subCategories[0];
			return defaultCategory ? [defaultCategory.id] : [];
		}

		const flattenedCategories = variant.budgetCategories.flatMap(
			(bc) => bc.subCategories
		);
		let filteredCategories = flattenedCategories
			.filter((c) => !c.isDeprecated)
			.map((c) => c.id);

		// If division has enabled categories, filter to only include those
		if (enabledCategories && enabledCategories.length > 0) {
			filteredCategories = filteredCategories.filter((c) =>
				enabledCategories.includes(c)
			);
		}

		return filteredCategories;
	}

	hasBudgets(): boolean {
		return (
			this.variants.some((v) => v.budgetCategories.length > 0) &&
			this.id !== "shortTime"
		);
	}

	abstract getDefaultSettings(): TSettings;
}

export class DefaultProduct extends Product<null> {
	getDefaultSettings(): null {
		return null;
	}

	static fromJson(data: any) {
		return new ProductDefaultDeserializer().deserialize(data);
	}
}
